const ATTR_FIRST = "bt_attr_first";
const ATTR_LAST = "bt_attr_last";
const SESSION_KEY = "bt_visit_session";
const VALID = /^[A-Za-z0-9._-]{1,100}$/;
const NINETY_DAYS = 90 * 24 * 60 * 60 * 1000;
const THIRTY_DAYS = 30 * 24 * 60 * 60 * 1000;
const THIRTY_MIN = 30 * 60 * 1000;
const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content"];
const STRIP_KEYS = [...UTM_KEYS, "utm_term"];
const CLICK_ID_KEYS = ["gclid", "wbraid", "gbraid"];
// First-party hostnames: internal navigation must never create or overwrite attribution
const SELF_DOMAINS = [window.location.hostname.toLowerCase().replace(/^www\./, ""), "thebiologytrader.com"];
// Bare Google search domains only (hostname already lowercased and www-stripped): google.com, google.de, google.co.uk, google.com.au
const GOOGLE_ORGANIC_RE = /^google\.(com|[a-z]{2}|co\.[a-z]{2}|com\.[a-z]{2})$/;

const readJson = (key) => {
  try {
    return JSON.parse(localStorage.getItem(key));
  } catch {
    return null;
  }
};
const writeJson = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {}
};
const readFresh = (key) => {
  const rec = readJson(key);
  if (!rec) return null;
  if (!(rec.expires_at && Date.now() < rec.expires_at)) {
    try {
      localStorage.removeItem(key);
    } catch {}
    return null;
  }
  return rec;
};

const domainMatch = (h, d) => h === d || h.endsWith("." + d);
const sanitizeHost = (h) => (h || "").toLowerCase().replace(/[^a-z0-9._-]/g, "").slice(0, 100);

const referrerAttribution = () => {
  const raw = document.referrer || "";
  if (!raw) return null;
  let u;
  try {
    u = new URL(raw);
  } catch {
    return null;
  }
  const mk = (source, medium, campaign) => ({ source, medium, campaign, content: "" });
  if (u.protocol === "android-app:") {
    const pkg = sanitizeHost(u.hostname || (u.pathname || "").replace(/^\/+/, "").split("/")[0]);
    if (!pkg || !VALID.test(pkg)) return null;
    if (pkg === "com.google.android.youtube") return mk("youtube", "referral", "");
    if (pkg === "com.reddit.frontpage") return mk("reddit", "referral", "");
    return mk(pkg, "referral", "");
  }
  if (u.protocol !== "http:" && u.protocol !== "https:") return null;
  const h = (u.hostname || "").toLowerCase().replace(/^www\./, "");
  if (!h) return null;
  if (SELF_DOMAINS.some((d) => d && domainMatch(h, d))) return null;
  if (GOOGLE_ORGANIC_RE.test(h)) return mk("google", "organic", "organic-search");
  if (domainMatch(h, "bing.com")) return mk("bing", "organic", "organic-search");
  if (domainMatch(h, "duckduckgo.com")) return mk("duckduckgo", "organic", "organic-search");
  if (domainMatch(h, "search.yahoo.com")) return mk("yahoo", "organic", "organic-search");
  if (domainMatch(h, "ecosia.org")) return mk("ecosia", "organic", "organic-search");
  if (domainMatch(h, "youtube.com") || domainMatch(h, "youtu.be")) return mk("youtube", "referral", "");
  if (domainMatch(h, "reddit.com") || domainMatch(h, "redd.it")) return mk("reddit", "referral", "");
  const host = sanitizeHost(h);
  return host && VALID.test(host) ? mk(host, "referral", "") : null;
};

const deriveArrival = (allowReferrer) => {
  const params = new URLSearchParams(window.location.search);
  const get = (key) => {
    const v = (params.get(key) || "").trim();
    return VALID.test(v) ? v : "";
  };
  const utmSource = get("utm_source");
  if (utmSource) {
    return {
      origin: "utm",
      source: utmSource.toLowerCase(),
      medium: get("utm_medium").toLowerCase(),
      campaign: get("utm_campaign").toLowerCase(),
      content: get("utm_content").toLowerCase(),
      raw: { source: utmSource, medium: get("utm_medium"), campaign: get("utm_campaign"), content: get("utm_content") },
    };
  }
  const hasClickId = CLICK_ID_KEYS.some((k) => {
    const v = (params.get(k) || "").trim();
    return v.length > 0 && v.length <= 200;
  });
  if (hasClickId) {
    return { origin: "clickid", source: "google", medium: "cpc", campaign: "google-ads", content: "" };
  }
  if (!allowReferrer) return null;
  const ref = referrerAttribution();
  if (ref) return { origin: "referrer", ...ref };
  return null;
};

const stripCapturedParams = () => {
  const params = new URLSearchParams(window.location.search);
  [...STRIP_KEYS, ...CLICK_ID_KEYS].forEach((k) => params.delete(k));
  const qs = params.toString();
  window.history.replaceState({}, "", window.location.pathname + (qs ? `?${qs}` : "") + window.location.hash);
};

export function captureAttribution({ allowReferrer = true } = {}) {
  const arrival = deriveArrival(allowReferrer);
  if (!arrival) return null;
  const now = Date.now();
  const rec = {
    source: arrival.source,
    medium: arrival.medium,
    campaign: arrival.campaign,
    content: arrival.content,
    origin: arrival.origin,
    entry_path: window.location.pathname,
    ...(arrival.raw ? { raw: arrival.raw } : {}),
    at: new Date().toISOString(),
  };
  if (!readFresh(ATTR_FIRST)) writeJson(ATTR_FIRST, { ...rec, expires_at: now + NINETY_DAYS });
  writeJson(ATTR_LAST, { ...rec, expires_at: now + THIRTY_DAYS });
  if (arrival.origin === "utm" || arrival.origin === "clickid") stripCapturedParams();
  return arrival;
}

export function getStoredAttribution() {
  const pick = (rec) =>
    rec
      ? { source: rec.source, medium: rec.medium, campaign: rec.campaign, content: rec.content, at: rec.at, entry_path: rec.entry_path }
      : null;
  return { first: pick(readFresh(ATTR_FIRST)), last: pick(readFresh(ATTR_LAST)) };
}

const randomId = () => {
  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
};

function getOrCreateSession() {
  const now = Date.now();
  let sess = readJson(SESSION_KEY);
  if (!sess || !sess.sid || now - (sess.last_activity || 0) > THIRTY_MIN) {
    sess = { sid: randomId(), last_activity: now, sources: [], entry_path: window.location.pathname };
  }
  return sess;
}

// Returns the current visit session id (same 30-minute window as trackVisit), creating one if needed.
export function getVisitSessionId() {
  const sess = getOrCreateSession();
  sess.last_activity = Date.now();
  if (!sess.entry_path) sess.entry_path = window.location.pathname;
  writeJson(SESSION_KEY, sess);
  return sess.sid;
}

export function trackVisit(apiBase, arrival) {
  const now = Date.now();
  let sess = getOrCreateSession();
  if (!sess.entry_path) sess.entry_path = window.location.pathname;
  sess.last_activity = now;
  const sources = sess.sources || [];
  const key = arrival ? arrival.source : "direct";
  const skip = arrival ? sources.includes(key) : sources.length > 0;
  if (!skip) sess.sources = [...sources, key];
  writeJson(SESSION_KEY, sess);
  if (skip) return;
  fetch(`${apiBase}/track/visit`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      source: key,
      medium: arrival ? arrival.medium || "" : "",
      campaign: arrival ? arrival.campaign || "" : "",
      content: arrival ? arrival.content || "" : "",
      session_id: sess.sid,
      entry_path: sess.entry_path || "",
    }),
    keepalive: true,
  }).catch(() => {});
}
